import { Router } from "@angular/router";
import { Injectable } from '@angular/core';
import { ApiService } from "./api.service";
import { environment } from '../../environments/environment';

export interface TokenResponse {
  access_token: string;
  backend_api_token?: string;
  refresh_token?: string;
  expires_in: number;
  token_type: string;
  id_token?: string;
}

interface UserInfo {
  sub: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  email?: string;
  preferred_username?: string;
  backend_api_token?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private clientId = environment.sso.clientId;
  private redirectUri = environment.sso.redirectWebUri;
  private redirectLocalUri = environment.sso.redirectLocalUri;
  private authUrl = environment.sso.authUrl;
  private tokenUrl = environment.sso.tokenUrl;
  private scope = environment.sso.scope;
  private grantType = environment.sso.grantType;
  private logoutUrl = environment.sso.logoutUrl;
  private local_trigger = false;

  constructor(private apiService: ApiService, private router: Router) {
    if (window.location.hostname === 'localhost') {
      this.redirectUri = this.redirectLocalUri;
      this.local_trigger = true;
    }
  }

  // Initiate login by redirecting to Azure AD authorization endpoint
  login() {
    // Save the current URL to redirect back after login
    if (!sessionStorage.getItem('redirectUrl')) {
      sessionStorage.setItem('redirectUrl', this.router.url);
    }

    // Build Azure AD authorization URL
    const authParams = new URLSearchParams({
      client_id: this.clientId,
      response_type: this.grantType,
      redirect_uri: this.redirectUri,
      scope: this.scope,
      response_mode: 'query',
      state: this.generateState()
    });

    // Redirect to Azure AD
    window.location.href = `${this.authUrl}?${authParams.toString()}`;
  }

  logout() {
    const postLogoutRedirectUri = this.local_trigger ? this.redirectLocalUri : this.redirectUri;
    
    // Clear all stored tokens and user data
    localStorage.clear();
    sessionStorage.clear();

    // Build Azure AD logout URL
    const logoutParams = new URLSearchParams({
      post_logout_redirect_uri: postLogoutRedirectUri
    });

    // Redirect to Azure AD logout
    window.location.href = `${this.logoutUrl}?${logoutParams.toString()}`;
  }

  // Exchange authorization code for tokens and store them
  async fetchToken(code: string): Promise<boolean> {
    try {
      console.log('Exchanging authorization code for tokens...');
      
    
      const data = await this.apiService.fetchAuthToken(code, this.local_trigger).toPromise();

      if (!data || !data.access_token) {
        console.error('No access token received from backend');
        localStorage.setItem('isLoading', 'false');
        this.logout();
        return false;
      }

      console.log('Tokens received successfully');

      // Store Azure AD access token
      localStorage.setItem('access_token', data.access_token);
      
      // Store backend API token if returned
      if (data.backend_api_token) {
        localStorage.setItem('backend_api_token', data.backend_api_token);
      }
      
      // Store refresh token
      if (data.refresh_token) {
        localStorage.setItem('refresh_token', data.refresh_token);
      }

      // Store ID token
      if (data.id_token) {
        localStorage.setItem('id_token', data.id_token);
      }

      // Calculate and store expiration time
      const expiresInMs = data.expires_in * 1000;
      const expiresAt = new Date(Date.now() + expiresInMs);
      localStorage.setItem('expires_at', expiresAt.toISOString());

      // Extract user info from ID token and store uid
      if (data.id_token) {
        this.extractUserInfoFromIdToken(data.id_token);
      }

      // If backend returns user info 
      if (data.sub) {
        localStorage.setItem('uid', data.sub);
        localStorage.setItem('user_id', data.sub);
      }
      if (data.email) localStorage.setItem('email', data.email);
      if (data.name) localStorage.setItem('name', data.name);
      if (data.given_name) localStorage.setItem('firstname', data.given_name);
      if (data.family_name) localStorage.setItem('lastname', data.family_name);
      if (data.preferred_username) localStorage.setItem('username', data.preferred_username);

      localStorage.setItem('isLoading', 'false');

      // Navigate to the originally requested page
      const redirectUrl = sessionStorage.getItem('redirectUrl') || '/';
      sessionStorage.removeItem('redirectUrl');
      this.router.navigateByUrl(redirectUrl);

      return true;
    } catch (error) {
      console.error('Error fetching token:', error);
      localStorage.setItem('isLoading', 'false');
      this.logout();
      return false;
    }
  }

  // Validate the current access token by calling backend
  async validateToken(consolelog?: string): Promise<boolean> {
    if (consolelog) {
      console.log(consolelog);
    }

    const accessToken = localStorage.getItem('access_token');

    if (!accessToken) {
      console.error('Access Token is not available.');
      localStorage.setItem('isLoading', 'false');
      this.login();
      return false;
    }

    try {
      console.log('Validating access token...');
      const userInfo = await this.apiService.validateAuthToken(accessToken).toPromise();

      if (!userInfo || !userInfo.sub) {
        console.error('Token validation failed - no user info returned');
        localStorage.setItem('isLoading', 'false');
        this.logout();
        return false;
      }

      console.log('Token validated successfully');

      // Store user information
      localStorage.setItem('uid', userInfo.sub);  
      localStorage.setItem('user_id', userInfo.sub);
      if (userInfo.email) localStorage.setItem('email', userInfo.email);
      if (userInfo.name) localStorage.setItem('name', userInfo.name);
      if (userInfo.given_name) localStorage.setItem('firstname', userInfo.given_name);
      if (userInfo.family_name) localStorage.setItem('lastname', userInfo.family_name);
      if (userInfo.preferred_username) localStorage.setItem('username', userInfo.preferred_username);
      
      // Store backend API token if returned
      if (userInfo.backend_api_token) {
        localStorage.setItem('backend_api_token', userInfo.backend_api_token);
      }

      localStorage.setItem('isLoading', 'false');

      return true;
    } catch (error: unknown) {
      console.error('Token validation error:', error);
      localStorage.setItem('isLoading', 'false');
      
      if (error instanceof Error && error.message === 'Unauthorized') {
        this.logout();
        return false;
      }

      return false;
    }
  }

  // Check if the current token is valid (not expired)
  isTokenValid(): boolean {
    const expiresAt = localStorage.getItem('expires_at');
    const accessToken = localStorage.getItem('access_token');
    const backendToken = localStorage.getItem('backend_api_token');

    if (!expiresAt || !accessToken || !backendToken) {
      return false;
    }

    const expiryDate = new Date(expiresAt);
    const now = new Date();

    // Add 5-minute buffer before expiration
    const bufferMs = 5 * 60 * 1000;
    return now.getTime() < (expiryDate.getTime() - bufferMs);
  }

  
  private extractUserInfoFromIdToken(idToken: string): void {
    try {
      const parts = idToken.split('.');
      if (parts.length !== 3) {
        console.error('Invalid ID token format');
        return;
      }

      // Decode the payload 
      const payload = JSON.parse(atob(parts[1]));
      
      // Store user information
      if (payload.sub) {
        localStorage.setItem('uid', payload.sub);  
        localStorage.setItem('user_id', payload.sub);
      }
      if (payload.email) localStorage.setItem('email', payload.email);
      if (payload.name) localStorage.setItem('name', payload.name);
      if (payload.given_name) localStorage.setItem('firstname', payload.given_name);
      if (payload.family_name) localStorage.setItem('lastname', payload.family_name);
      if (payload.preferred_username) localStorage.setItem('username', payload.preferred_username);
    } catch (error) {
      console.error('Error parsing ID token:', error);
    }
  }

  
  private generateState(): string {
    const state = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    sessionStorage.setItem('oauth_state', state);
    return state;
  }

  
  verifyState(returnedState: string): boolean {
    const savedState = sessionStorage.getItem('oauth_state');
    sessionStorage.removeItem('oauth_state');
    return savedState === returnedState;
  }
}